import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import {
  ArrowRight, Phone, Globe, MapPin, Star, Trash2, Loader2, Target, Zap, Plus, Search, X, Sparkles,
} from "lucide-react";
import AddLeadModal from "../components/AddLeadModal";
import ConfirmModal from "../components/ConfirmModal";

const isIcp = (l) =>
  !l.website ||
  l.website.includes("google.com/maps") ||
  l.website_quality === "bad" ||
  l.website_quality === "none";

const STAGES = ["scraped", "contacted", "answered", "qualified", "booked", "demo", "won", "lost"];
const AI_LABELS = ["Hot", "Warm", "Cold"];

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [icpOnly, setIcpOnly] = useState(false);
  const [campaignFilter, setCampaignFilter] = useState("");
  const [stageFilter, setStageFilter] = useState("");
  const [aiLabelFilter, setAiLabelFilter] = useState("");
  const [classifiedFilter, setClassifiedFilter] = useState("");  // "" | "yes" | "no"
  const [blasting, setBlasting] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkScoring, setBulkScoring] = useState(false);
  // Custom confirm modal state — replaces native window.confirm/prompt which
  // mobile browsers + some incognito sessions can silently block.
  const [confirmModal, setConfirmModal] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (icpOnly) params.icp_only = true;
      if (campaignFilter) params.campaign_id = campaignFilter;
      if (stageFilter) params.stage = stageFilter;
      if (aiLabelFilter) params.ai_label = aiLabelFilter;
      if (classifiedFilter) params.classified = classifiedFilter;
      const { data } = await api.get("/leads", { params });
      setLeads(data);
      setSelected(new Set());
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadCampaigns = async () => {
    try {
      const { data } = await api.get("/campaigns");
      setCampaigns(data);
    } catch (e) { /* non-fatal */ }
  };

  useEffect(() => { loadCampaigns(); }, []);
  useEffect(() => { load(); }, [icpOnly, campaignFilter, stageFilter, aiLabelFilter, classifiedFilter]);

  const anyFilterActive = Boolean(
    icpOnly || campaignFilter || stageFilter || aiLabelFilter || classifiedFilter || search
  );
  const clearAllFilters = () => {
    setIcpOnly(false);
    setCampaignFilter("");
    setStageFilter("");
    setAiLabelFilter("");
    setClassifiedFilter("");
    setSearch("");
  };

  // Client-side search across name, city, phone, niche, website
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter((l) => {
      const hay = [
        l.name, l.city, l.state, l.phone, l.email,
        l.niche, l.website, l.address,
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [leads, search]);

  const icpCount = useMemo(() => leads.filter(isIcp).length, [leads]);
  const allVisibleSelected = filtered.length > 0 && filtered.every((l) => selected.has(l.id));
  const selectedCount = selected.size;

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelected((prev) => {
      if (allVisibleSelected) {
        const next = new Set(prev);
        filtered.forEach((l) => next.delete(l.id));
        return next;
      }
      const next = new Set(prev);
      filtered.forEach((l) => next.add(l.id));
      return next;
    });
  };

  const deleteLead = (lead, e) => {
    e.stopPropagation();
    setConfirmModal({
      title: "Delete lead",
      body: `Delete "${lead.name}"? Its activity history will also be removed.`,
      destructive: true,
      confirmLabel: "Delete",
      onConfirm: async () => {
        setDeletingId(lead.id);
        try {
          await api.delete(`/leads/${lead.id}`);
          setLeads((prev) => prev.filter((l) => l.id !== lead.id));
          setSelected((prev) => { const n = new Set(prev); n.delete(lead.id); return n; });
          toast.success("Lead deleted");
        } catch (err) {
          toast.error(err.response?.data?.detail || "Delete failed");
        } finally {
          setDeletingId(null);
        }
      },
    });
  };

  const bulkDeleteSelected = () => {
    if (selectedCount === 0) return;
    setConfirmModal({
      title: `Delete ${selectedCount} selected lead${selectedCount === 1 ? "" : "s"}?`,
      body: "Their activity history will also be removed. This cannot be undone.",
      destructive: true,
      confirmLabel: `Delete ${selectedCount}`,
      onConfirm: async () => {
        setBulkDeleting(true);
        try {
          const { data } = await api.post("/leads/bulk-delete", {
            scope: "ids",
            lead_ids: Array.from(selected),
          });
          toast.success(`Deleted ${data.deleted} lead(s)`);
          await load();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Bulk delete failed");
        } finally {
          setBulkDeleting(false);
        }
      },
    });
  };

  const wipeAll = () => {
    if (leads.length === 0) return;
    const phrase = `DELETE ${leads.length}`;
    setConfirmModal({
      title: `Wipe ALL ${leads.length} leads?`,
      body: `This deletes every lead you own (regardless of search/filter) AND all of their activity history. Cannot be undone.`,
      destructive: true,
      confirmLabel: "Wipe everything",
      requirePhrase: phrase,
      onConfirm: async () => {
        setBulkDeleting(true);
        try {
          const { data } = await api.post("/leads/bulk-delete", { scope: "all" });
          toast.success(`Wiped ${data.deleted} lead(s)`);
          await load();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Wipe failed");
        } finally {
          setBulkDeleting(false);
        }
      },
    });
  };
  const bulkScoreSelected = () => {
    if (selectedCount === 0) return;
    const ids = Array.from(selected);
    const unscored = leads.filter((l) => ids.includes(l.id) && l.ai_score == null).length;
    const willOverwrite = ids.length - unscored;
    setConfirmModal({
      title: `AI-score ${selectedCount} selected lead${selectedCount === 1 ? "" : "s"}?`,
      body: (
        <ul className="list-disc list-inside space-y-1 text-sm text-zinc-700">
          <li>Claude reads each lead's website + niche and assigns a 0-100 score + Hot/Warm/Cold label.</li>
          <li>Generates a custom SMS, call script, and email template per lead.</li>
          {willOverwrite > 0 && (
            <li className="text-amber-700">
              <b>{willOverwrite}</b> lead{willOverwrite === 1 ? " already has" : "s already have"} a score — will be overwritten.
            </li>
          )}
        </ul>
      ),
      destructive: false,
      confirmLabel: `Score ${selectedCount}`,
      onConfirm: async () => {
        setBulkScoring(true);
        try {
          const { data } = await api.post("/leads/bulk-classify", { lead_ids: ids });
          const failed = (data.failed || []).length;
          if (failed === 0) {
            toast.success(`Scored ${data.processed}/${data.requested} lead${data.requested === 1 ? "" : "s"}`);
          } else {
            toast.warning(`${data.processed}/${data.requested} scored · ${failed} failed (see console)`);
            console.warn("Bulk-classify failures:", data.failed);
          }
          setSelected(new Set());
          await load();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Bulk AI score failed");
        } finally {
          setBulkScoring(false);
        }
      },
    });
  };



  const bulkBlast = () => {
    if (icpCount === 0) {
      toast.error("No ICP leads to blast — toggle ICP and scrape more first");
      return;
    }
    setConfirmModal({
      title: `Blast ${icpCount} ICP lead${icpCount === 1 ? "" : "s"}?`,
      body: (
        <ul className="list-disc list-inside space-y-1">
          <li>Generate a custom SMS per lead (mentions their bad/missing website)</li>
          <li>Send via Twilio immediately</li>
          <li>Queue an outbound Vapi call (with your sequence delay)</li>
        </ul>
      ),
      destructive: false,
      confirmLabel: `Blast ${icpCount}`,
      onConfirm: async () => {
        setBlasting(true);
        try {
          const { data } = await api.post("/leads/bulk-blast", { scope: "icp" });
          const errCount = (data.errors || []).length;
          if (errCount === 0) {
            toast.success(
              `Blast complete: ${data.sent} SMS sent, ${data.scheduled} calls queued (in ${data.follow_up_call_in_hours}h)`
            );
          } else {
            toast.warning(
              `${data.sent}/${data.matched} SMS sent · ${errCount} errors (see console)`
            );
            console.warn("Bulk-blast errors:", data.errors);
          }
          await load();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Bulk-blast failed");
        } finally {
          setBlasting(false);
        }
      },
    });
  };

  return (
    <div className="p-8 max-w-[1400px]" data-testid="leads-page">
      <div className="border-b border-zinc-200 pb-6 mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline mb-2">/ all leads</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black tracking-tight">Leads</h1>
          <div className="text-xs text-zinc-500 font-mono mt-1">
            {filtered.length} of {leads.length} · {icpCount} ICP-ready
            {selectedCount > 0 && <span className="ml-2 text-[#002FA7]">· {selectedCount} selected</span>}
          </div>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <select
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
            data-testid="leads-campaign-filter"
            className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-300 bg-white"
            title="Filter leads by campaign"
          >
            <option value="">All Campaigns</option>
            {campaigns.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          <button
            onClick={() => setIcpOnly((v) => !v)}
            data-testid="leads-icp-toggle"
            className={`px-3 py-2 text-xs font-bold uppercase tracking-widest border flex items-center gap-1.5 transition ${
              icpOnly
                ? "bg-red-600 border-red-600 text-white"
                : "bg-white border-zinc-300 text-zinc-700 hover:border-zinc-400"
            }`}
            title={campaignFilter ? "Apply this campaign's ICP rules" : "Show only leads matching ICP (bad/no website)"}
          >
            <Target className="w-3.5 h-3.5" /> ICP Only
          </button>
          {icpOnly && leads.length > 0 && (
            <button
              onClick={bulkBlast}
              disabled={blasting}
              data-testid="leads-bulk-blast"
              className="px-4 py-2 text-xs font-bold uppercase tracking-widest bg-black hover:bg-zinc-800 text-white flex items-center gap-1.5 disabled:opacity-50"
              title={`Send AI-personalized SMS to ${leads.length} ICP leads now + queue follow-up calls`}
            >
              {blasting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
              {blasting ? "Blasting…" : `Blast ICP (${leads.length})`}
            </button>
          )}
          <button
            onClick={() => setShowAddModal(true)}
            data-testid="leads-cta-add"
            className="border border-zinc-300 hover:bg-zinc-100 text-zinc-700 px-3 py-2 text-xs font-bold uppercase tracking-widest flex items-center gap-1.5"
          >
            <Plus className="w-3.5 h-3.5" /> Add Lead
          </button>
          <button
            onClick={() => navigate("/scraper")}
            data-testid="leads-cta-scrape"
            className="bg-[#002FA7] hover:bg-[#00227A] text-white px-4 py-2 text-xs font-bold uppercase tracking-widest"
          >
            Scrape More
          </button>
        </div>
      </div>

      {/* Filter row */}
      <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="leads-filter-row">
        <span className="overline text-zinc-500 mr-1">/ filters</span>
        <select
          value={stageFilter}
          onChange={(e) => setStageFilter(e.target.value)}
          data-testid="leads-stage-filter"
          className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-300 bg-white"
          title="Filter by pipeline stage"
        >
          <option value="">All Stages</option>
          {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select
          value={aiLabelFilter}
          onChange={(e) => setAiLabelFilter(e.target.value)}
          data-testid="leads-ai-label-filter"
          className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-300 bg-white"
          title="Filter by AI classification label (Hot/Warm/Cold)"
        >
          <option value="">All AI Labels</option>
          {AI_LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
        </select>
        <select
          value={classifiedFilter}
          onChange={(e) => setClassifiedFilter(e.target.value)}
          data-testid="leads-classified-filter"
          className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-300 bg-white"
          title="Show only classified (AI-scored) or unclassified leads"
        >
          <option value="">Classified: Any</option>
          <option value="yes">Classified only</option>
          <option value="no">Unclassified only</option>
        </select>
        {anyFilterActive && (
          <button
            type="button"
            onClick={clearAllFilters}
            data-testid="leads-filters-clear"
            className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-zinc-300 text-zinc-700 hover:bg-zinc-100 flex items-center gap-1"
            title="Clear every filter and search"
          >
            <X className="w-3 h-3" /> Clear filters
          </button>
        )}
      </div>

      {/* Toolbar: search + bulk actions */}
      <div className="mb-3 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="leads-search-input"
            placeholder="Search by name, city, phone, niche, website…"
            className="w-full pl-9 pr-9 py-2 text-sm border border-zinc-300 focus:outline-none focus:border-[#002FA7] focus:ring-2 focus:ring-[#002FA7]/20 bg-white"
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              data-testid="leads-search-clear"
              className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700"
              title="Clear search"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
        {selectedCount > 0 && (
          <button
            onClick={bulkScoreSelected}
            disabled={bulkScoring || bulkDeleting}
            data-testid="leads-bulk-score-selected"
            className="px-3 py-2 text-xs font-bold uppercase tracking-widest bg-[#002FA7] hover:bg-[#001E70] text-white flex items-center gap-1.5 disabled:opacity-50"
            title="AI-score the selected leads (overwrites existing scores)"
          >
            {bulkScoring ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            AI Score {selectedCount}
          </button>
        )}
        {selectedCount > 0 && (
          <button
            onClick={bulkDeleteSelected}
            disabled={bulkDeleting}
            data-testid="leads-bulk-delete-selected"
            className="px-3 py-2 text-xs font-bold uppercase tracking-widest bg-red-600 hover:bg-red-700 text-white flex items-center gap-1.5 disabled:opacity-50"
          >
            {bulkDeleting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
            Delete {selectedCount} selected
          </button>
        )}
        {leads.length > 0 && (
          <button
            onClick={wipeAll}
            disabled={bulkDeleting}
            data-testid="leads-wipe-all"
            className="px-3 py-2 text-xs font-bold uppercase tracking-widest border border-red-300 text-red-700 hover:bg-red-50 flex items-center gap-1.5 disabled:opacity-50"
            title="Permanently delete every lead you own. Requires typed confirmation."
          >
            <Trash2 className="w-3.5 h-3.5" />
            Wipe All ({leads.length})
          </button>
        )}
      </div>

      <div className="border border-zinc-200 bg-white">
        {loading ? (
          <div className="px-5 py-12 text-center text-sm text-zinc-500 font-mono">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="px-5 py-12 text-center" data-testid="leads-empty">
            <div className="font-heading text-lg font-bold">
              {search ? "No matches" : icpOnly ? "No ICP leads yet" : "No leads yet"}
            </div>
            <div className="text-sm text-zinc-500 mt-1">
              {search
                ? `Nothing matches "${search}". Try a different term.`
                : icpOnly
                  ? "Toggle off ICP Only to see all leads, or scrape more to find venues with bad/no websites."
                  : "Go to the Scraper to discover local businesses."}
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50">
                <th className="text-left px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    data-testid="leads-select-all"
                    className="w-3.5 h-3.5 accent-[#002FA7] cursor-pointer"
                    title={allVisibleSelected ? "Deselect all visible" : "Select all visible"}
                  />
                </th>
                <th className="text-left px-4 py-3 overline">Business</th>
                <th className="text-left px-4 py-3 overline">Niche</th>
                <th className="text-left px-4 py-3 overline">Contact</th>
                <th className="text-left px-4 py-3 overline">Rating</th>
                <th className="text-left px-4 py-3 overline">Stage</th>
                <th className="text-left px-4 py-3 overline">AI</th>
                <th className="text-right px-4 py-3 overline">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((lead) => (
                <tr key={lead.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`leads-row-${lead.id}`}>
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(lead.id)}
                      onChange={() => toggleOne(lead.id)}
                      data-testid={`leads-select-${lead.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="w-3.5 h-3.5 accent-[#002FA7] cursor-pointer"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Link
                      to={`/leads/${lead.id}`}
                      data-testid={`lead-name-link-${lead.id}`}
                      className="font-semibold flex items-center gap-1.5 text-zinc-900 hover:text-[#002FA7] hover:underline underline-offset-2 transition-colors"
                    >
                      {lead.name}
                      {isIcp(lead) && (
                        <span className="text-[9px] px-1 py-0.5 bg-red-100 text-red-700 font-bold uppercase tracking-wider" title="No website or poor-quality website — your ideal customer">
                          ICP
                        </span>
                      )}
                    </Link>
                    <div className="flex items-center gap-1 text-xs text-zinc-500 mt-0.5">
                      <MapPin className="w-3 h-3" /> {lead.city}{lead.state ? `, ${lead.state}` : ""}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs font-mono">{lead.niche}</td>
                  <td className="px-4 py-3 font-mono text-xs">
                    {lead.phone && <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {lead.phone}</div>}
                    {lead.website && <div className="flex items-center gap-1 text-zinc-500"><Globe className="w-3 h-3" /> {lead.website.replace(/^https?:\/\//, "").slice(0, 25)}</div>}
                  </td>
                  <td className="px-4 py-3">
                    {lead.rating ? (
                      <div className="flex items-center gap-1 text-xs">
                        <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                        <span className="font-mono">{lead.rating}</span>
                      </div>
                    ) : <span className="text-xs text-zinc-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-zinc-100 border border-zinc-200">
                      {lead.stage}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {lead.ai_score != null ? (
                      <div className="flex items-center gap-1">
                        <span className="px-1.5 py-0.5 text-[9px] font-bold uppercase" style={{
                          background: lead.ai_label === "Hot" ? "#EF4444" : lead.ai_label === "Warm" ? "#F59E0B" : "#52525B",
                          color: "white",
                        }}>
                          {lead.ai_label}
                        </span>
                        <span className="font-mono text-xs font-bold">{lead.ai_score}</span>
                      </div>
                    ) : <span className="text-xs text-zinc-400 font-mono">unscored</span>}
                    {lead.qualified_suggestion && (
                      <div
                        className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                          lead.qualified_suggestion.label === "interested" || lead.qualified_suggestion.label === "positive"
                            ? "bg-emerald-100 text-emerald-800"
                            : lead.qualified_suggestion.label === "not_interested" || lead.qualified_suggestion.label === "unsubscribe" || lead.qualified_suggestion.label === "negative"
                            ? "bg-red-100 text-red-800"
                            : lead.qualified_suggestion.label === "qualified"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-zinc-200 text-zinc-700"
                        }`}
                        title="AI auto-qualify suggestion from latest inbound — open the lead to accept/dismiss"
                        data-testid={`lead-intent-${lead.id}`}
                      >
                        ai: {String(lead.qualified_suggestion.label || "").replace(/_/g, " ")}
                        {typeof lead.qualified_suggestion.confidence === "number" &&
                          ` ${Math.round(lead.qualified_suggestion.confidence * 100)}%`}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <button
                        onClick={(e) => deleteLead(lead, e)}
                        disabled={deletingId === lead.id}
                        data-testid={`leads-delete-${lead.id}`}
                        className="text-zinc-400 hover:text-red-600 transition-colors disabled:opacity-50"
                        title="Delete lead"
                      >
                        {deletingId === lead.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      </button>
                      <Link
                        to={`/leads/${lead.id}`}
                        data-testid={`lead-open-${lead.id}`}
                        className="text-xs font-bold uppercase tracking-wider text-[#002FA7] hover:text-[#00227A] flex items-center gap-1"
                      >
                        Open <ArrowRight className="w-3 h-3" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <AddLeadModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onCreated={() => load()}
      />
      <ConfirmModal modal={confirmModal} onClose={() => setConfirmModal(null)} />
    </div>
  );
}
